import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { getAuthSiteUrl } from '../authSiteUrl';
import { useNavigate } from 'react-router-dom';
import { runPostAuthOnboarding } from '../services/onboardingClient';
import { devError } from '../utils/logger';
import LogoImg from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';

const Logo = () => (
  <span className="flex items-center justify-center mb-3 md:mb-4" style={{ alignItems: 'center' }}>
    <img src={LogoImg} alt="DevelloTech Logo" style={{ width: 96, height: 96, objectFit: 'contain', marginRight: 0, padding: 0, display: 'block' }} />
    <span
      className="text-3xl font-bold tracking-tight"
      style={{
        background: 'linear-gradient(90deg, #1761A0 0%, #2ED8C3 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        marginLeft: -12,
        paddingLeft: 0,
      }}
    >
      DevelloTech
    </span>
  </span>
);

const VerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [profileSetupFailed, setProfileSetupFailed] = useState(false);
  const navigate = useNavigate();
  const postVerifyNavigated = useRef(false);

  const navigateAfterEmailVerified = useCallback(() => {
    if (postVerifyNavigated.current) return;
    postVerifyNavigated.current = true;
    runPostAuthOnboarding()
      .then((next) => {
        if (next === 'dashboard') {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch((error) => {
        devError('PROFILE_CREATION_FAILED', error);
        setError('Could not initialize your account. Please try signing in again.');
        setProfileSetupFailed(true);
        postVerifyNavigated.current = false;
      });
  }, [navigate]);

  useEffect(() => {
    if (!supabase) return undefined;
    supabase.auth.getSession().catch(() => {});
    return undefined;
  }, []);

  useEffect(() => {
    if (!supabase) return undefined;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (!currentUser) {
        postVerifyNavigated.current = false;
        navigate('/account');
        return;
      }
      if (currentUser.email_confirmed_at) {
        navigateAfterEmailVerified();
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, navigateAfterEmailVerified]);

  // REMOVED: Auto-send verification code to prevent "too many requests" error
  // useEffect(() => {
  //   if (user && !user.emailVerified && !codeSent) {
  //     handleSendVerificationCode();
  //   }
  // }, [user, codeSent]);

  // Countdown timer for resend button
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendVerificationCode = async () => {
    setLoading(true);
    setError('');
    try {
      if (user) {
        const { error: resendErr } = await supabase.auth.resend({
          type: 'signup',
          email: user.email,
          options: { emailRedirectTo: `${getAuthSiteUrl()}/verify-email` },
        });
        if (resendErr) throw resendErr;
        setSuccess('Verification link sent! Please check your email.');
        setCountdown(60);
      } else {
        setError('No user found. Please sign in first.');
      }
    } catch (err) {
      devError('Verification error:', err);
      const msg = err.message || '';
      if (msg.includes('rate limit') || msg.includes('too many')) {
        setError('Too many verification attempts. Please wait 5 minutes before trying again.');
      } else {
        setError('Failed to send verification link. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    setError('');
    setProfileSetupFailed(false);
    try {
      const { data: { user: refreshedUser }, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;

      if (refreshedUser?.email_confirmed_at) {
        setSuccess('Email verified. Taking you to your workspace…');
        setTimeout(() => {
          navigateAfterEmailVerified();
        }, 800);
      } else {
        setError('Email not yet verified. Please check your email and click the verification link.');
      }
    } catch (err) {
      devError('Verification check error:', err);
      setError('Failed to check verification status. Please try again.');
    }
    setLoading(false);
  };

  const handleSignOut = async () => {
    try {
      postVerifyNavigated.current = false;
      await supabase.auth.signOut();
      navigate('/account');
    } catch (err) {
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleRetryProfileSetup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setProfileSetupFailed(false);
    try {
      const next = await runPostAuthOnboarding();
      if (next === 'dashboard') {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      devError('PROFILE_CREATION_FAILED', err);
      setProfileSetupFailed(true);
      setError('Could not initialize your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-8 shadow-strong max-w-md w-full text-center">
          <Logo />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Email Verification</h2>
          <p className="text-gray-600 mb-6">Please sign in to verify your email address.</p>
          <button
            onClick={() => navigate('/account')}
            className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl p-8 shadow-strong max-w-md w-full text-center">
        <Logo />
        
        {user.email_confirmed_at ? (
          <>
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600">Your email address has been successfully verified.</p>
            </div>
            <button
              type="button"
              onClick={() => navigateAfterEmailVerified()}
              className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              Continue to workspace
            </button>
          </>
        ) : (
          <>
            <div className="mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
              <p className="text-gray-600 mb-4">
                We've sent a verification link to <strong>{user.email}</strong>
              </p>
              <p className="text-sm text-gray-500">
                Please check your inbox and click the verification link in the email.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            {profileSetupFailed && (
              <button
                onClick={handleRetryProfileSetup}
                disabled={loading}
                className="mb-4 w-full bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Retry account setup
              </button>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{success}</p>
              </div>
            )}

            <div className="mb-6">
              <button
                onClick={handleCheckVerification}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Checking...' : 'I\'ve Verified My Email'}
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleSendVerificationCode}
                disabled={loading || countdown > 0}
                className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Email'}
              </button>
              
              <button
                onClick={handleSignOut}
                className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:bg-gray-200"
              >
                Sign Out
              </button>
            </div>

            <div className="mt-6 text-sm text-gray-500">
              <p>Didn't receive the email?</p>
              <ul className="mt-2 space-y-1">
                <li>• Check your spam folder</li>
                <li>• Make sure you entered the correct email address</li>
                <li>• Try clicking "Resend Email" above</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail; 